import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import MediaUpload from './MediaUpload';
import { SAP_ICON_NAMES } from './sapIcons';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Save, RotateCcw,
  Loader2, GripVertical, ExternalLink, Monitor, Smartphone, X
} from 'lucide-react';

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const SECTION_TYPES = [
  { value: 'richtext', label: 'Texte (WYSIWYG)' },
  { value: 'callout', label: 'Encadré' },
  { value: 'list', label: 'Liste à puces' },
  { value: 'steps', label: 'Étapes' },
  { value: 'faq', label: 'FAQ' },
  { value: 'buttons', label: 'Boutons / Liens' },
  { value: 'image', label: 'Image' },
];

const TYPE_LABEL = Object.fromEntries(SECTION_TYPES.map((t) => [t.value, t.label]));

const Field = ({ label, value, onChange, placeholder, type = 'text', testId }) => (
  <div>
    {label && <label className="text-sm font-medium text-[#2A312C] mb-1 block">{label}</label>}
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="input-default"
      data-testid={testId}
    />
  </div>
);

const Toggle = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 rounded-full transition-colors ${value ? 'bg-[#A5BBA8]' : 'bg-[#E3DED5]'}`}
      data-testid={testId}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${value ? 'translate-x-5' : ''}`} />
    </button>
    <span className="text-sm text-[#2A312C]">{label}</span>
  </label>
);

const ColorField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-sm font-medium text-[#2A312C] mb-1 block">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#A5BBA8'}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border border-[#E3DED5] cursor-pointer p-0.5 bg-white"
        data-testid={testId}
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#A5BBA8"
        className="input-default flex-1"
      />
      {value && (
        <button type="button" onClick={() => onChange('')} className="text-xs text-[#C86B51] px-2">Réinit.</button>
      )}
    </div>
  </div>
);

function SectionEditor({ section, index, total, onChange, onMove, onDelete }) {
  const up = (patch) => onChange({ ...section, ...patch });

  const updateArr = (key, i, patch) => {
    const arr = [...(section[key] || [])];
    arr[i] = typeof patch === 'string' ? patch : { ...arr[i], ...patch };
    up({ [key]: arr });
  };
  const addArr = (key, item) => up({ [key]: [...(section[key] || []), item] });
  const removeArr = (key, i) => up({ [key]: (section[key] || []).filter((_, idx) => idx !== i) });

  return (
    <div className="border border-[#E3DED5] rounded-xl p-4 bg-white" data-testid={`sap-section-editor-${index}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-[#F0EBE0]">
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="w-4 h-4 text-[#C4BCA d]" />
          <span className="text-xs font-semibold uppercase tracking-wide text-[#A5BBA8] bg-[#A5BBA8]/10 px-2 py-1 rounded">
            {TYPE_LABEL[section.type] || section.type}
          </span>
          <span className="text-sm text-[#2A312C] truncate">{section.title || '(sans titre)'}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}
            className="p-1.5 rounded hover:bg-[#F9F6F0] disabled:opacity-30" title="Monter" data-testid={`section-up-${index}`}>
            <ChevronUp className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1}
            className="p-1.5 rounded hover:bg-[#F9F6F0] disabled:opacity-30" title="Descendre" data-testid={`section-down-${index}`}>
            <ChevronDown className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => up({ visible: !(section.visible !== false) })}
            className="p-1.5 rounded hover:bg-[#F9F6F0]" title={section.visible !== false ? 'Masquer' : 'Afficher'} data-testid={`section-visible-${index}`}>
            {section.visible !== false ? <Eye className="w-4 h-4 text-[#A5BBA8]" /> : <EyeOff className="w-4 h-4 text-[#C4BCAD]" />}
          </button>
          <button type="button" onClick={() => onDelete(index)}
            className="p-1.5 rounded hover:bg-red-50 text-[#C86B51]" title="Supprimer" data-testid={`section-delete-${index}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <Field label="Titre de la section" value={section.title} onChange={(v) => up({ title: v })} testId={`section-title-${index}`} />

        {/* Icon + colors */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-1 block">Icône (téléversée)</label>
            <MediaUpload testIdPrefix={`section-icon-${index}`} value={section.icon_url}
              onChange={(url) => up({ icon_url: url })} />
            <div className="mt-2">
              <label className="text-xs text-[#5C6A61] mb-1 block">…ou icône de la bibliothèque</label>
              <select value={section.icon_name || ''} onChange={(e) => up({ icon_name: e.target.value })}
                className="input-default" data-testid={`section-iconname-${index}`}>
                <option value="">Aucune</option>
                {SAP_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-3">
            <ColorField label="Couleur d'accent (icône, puces)" value={section.accent_color} onChange={(v) => up({ accent_color: v })} testId={`section-accent-${index}`} />
            {section.type === 'callout' && (
              <ColorField label="Couleur de fond de l'encadré" value={section.bg_color} onChange={(v) => up({ bg_color: v })} testId={`section-bg-${index}`} />
            )}
          </div>
        </div>

        {/* Type-specific editors */}
        {(section.type === 'richtext' || section.type === 'callout') && (
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-1 block">Contenu</label>
            <ReactQuill theme="snow" value={section.body_html || ''} onChange={(v) => up({ body_html: v })} modules={QUILL_MODULES} />
          </div>
        )}

        {section.type === 'list' && (
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-2 block">Éléments de la liste</label>
            <div className="space-y-2">
              {(section.items || []).map((item, i) => (
                <div key={i} className="flex gap-2">
                  <input value={item} onChange={(e) => updateArr('items', i, e.target.value)} className="input-default flex-1" data-testid={`section-${index}-item-${i}`} />
                  <button type="button" onClick={() => removeArr('items', i)} className="p-2 text-[#C86B51] hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addArr('items', 'Nouvel élément')} className="mt-2 text-sm text-[#A5BBA8] flex items-center gap-1"><Plus className="w-4 h-4" />Ajouter un élément</button>
          </div>
        )}

        {section.type === 'steps' && (
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-2 block">Étapes</label>
            <div className="space-y-3">
              {(section.steps || []).map((step, i) => (
                <div key={i} className="p-3 bg-[#F9F6F0] rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#5C6A61]">Étape {i + 1}</span>
                    <button type="button" onClick={() => removeArr('steps', i)} className="text-[#C86B51]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input value={step.title || ''} onChange={(e) => updateArr('steps', i, { title: e.target.value })} placeholder="Titre" className="input-default" data-testid={`section-${index}-step-title-${i}`} />
                  <textarea value={step.text || ''} onChange={(e) => updateArr('steps', i, { text: e.target.value })} placeholder="Description" className="input-default" rows={2} />
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addArr('steps', { title: 'Nouvelle étape', text: '' })} className="mt-2 text-sm text-[#A5BBA8] flex items-center gap-1"><Plus className="w-4 h-4" />Ajouter une étape</button>
          </div>
        )}

        {section.type === 'faq' && (
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-2 block">Questions / Réponses</label>
            <div className="space-y-3">
              {(section.faq || []).map((item, i) => (
                <div key={i} className="p-3 bg-[#F9F6F0] rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#5C6A61]">Question {i + 1}</span>
                    <button type="button" onClick={() => removeArr('faq', i)} className="text-[#C86B51]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input value={item.question || ''} onChange={(e) => updateArr('faq', i, { question: e.target.value })} placeholder="Question" className="input-default" data-testid={`section-${index}-faq-q-${i}`} />
                  <textarea value={item.answer || ''} onChange={(e) => updateArr('faq', i, { answer: e.target.value })} placeholder="Réponse" className="input-default" rows={2} />
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addArr('faq', { question: 'Nouvelle question', answer: '', order: (section.faq?.length || 0) + 1 })} className="mt-2 text-sm text-[#A5BBA8] flex items-center gap-1"><Plus className="w-4 h-4" />Ajouter une question</button>
          </div>
        )}

        {section.type === 'buttons' && (
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-2 block">Boutons / Liens</label>
            <div className="space-y-3">
              {(section.buttons || []).map((btn, i) => (
                <div key={i} className="p-3 bg-[#F9F6F0] rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#5C6A61] flex items-center gap-1"><ExternalLink className="w-3 h-3" />Lien {i + 1}</span>
                    <button type="button" onClick={() => removeArr('buttons', i)} className="text-[#C86B51]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input value={btn.text || ''} onChange={(e) => updateArr('buttons', i, { text: e.target.value })} placeholder="Texte du bouton" className="input-default" data-testid={`section-${index}-btn-text-${i}`} />
                  <input value={btn.url || ''} onChange={(e) => updateArr('buttons', i, { url: e.target.value })} placeholder="https://..." className="input-default" data-testid={`section-${index}-btn-url-${i}`} />
                  <select value={btn.style || 'primary'} onChange={(e) => updateArr('buttons', i, { style: e.target.value })} className="input-default">
                    <option value="primary">Principal (foncé)</option>
                    <option value="secondary">Secondaire (vert)</option>
                    <option value="outline">Contour</option>
                  </select>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addArr('buttons', { text: 'Nouveau lien', url: '', style: 'primary' })} className="mt-2 text-sm text-[#A5BBA8] flex items-center gap-1"><Plus className="w-4 h-4" />Ajouter un lien</button>
          </div>
        )}

        {section.type === 'image' && (
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-1 block">Image / Illustration</label>
            <MediaUpload testIdPrefix={`section-image-${index}`} value={section.image_url} onChange={(url) => up({ image_url: url })} optimize={true} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function SAPPageEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState('richtext');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewKey, setPreviewKey] = useState(0);

  const openPreview = () => {
    // Pass current (unsaved) content to the preview tab via localStorage (shared across tabs, same origin).
    try {
      localStorage.setItem('sap_preview_content', JSON.stringify(content));
    } catch (e) { /* ignore quota */ }
    // Open in a new tab so the (Quill-heavy) editor tree is not re-rendered and its state is preserved.
    window.open('/sap-preview', '_blank', 'noopener');
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await adminApi.getSAPPageContent();
      setContent(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const set = (patch) => setContent((prev) => ({ ...prev, ...patch }));

  const commitSections = (sections) => {
    const withOrder = sections.map((s, i) => ({ ...s, order: i + 1 }));
    set({ sections: withOrder });
  };

  const updateSection = (index, updated) => {
    const arr = [...content.sections];
    arr[index] = updated;
    set({ sections: arr });
  };

  const moveSection = (index, dir) => {
    const arr = [...content.sections];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    commitSections(arr);
  };

  const deleteSection = (index) => {
    if (!window.confirm('Supprimer cette section ?')) return;
    commitSections(content.sections.filter((_, i) => i !== index));
  };

  const addSection = () => {
    const id = `sec_${Math.random().toString(36).slice(2, 10)}`;
    const base = { id, type: newType, title: 'Nouvelle section', visible: true, body_html: '', items: [], steps: [], faq: [], buttons: [], icon_name: '', order: (content.sections?.length || 0) + 1 };
    commitSections([...(content.sections || []), base]);
    toast.success('Section ajoutée en bas de page');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSAPPageContent(content);
      toast.success('Page SAP mise à jour !');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser la page avec le contenu conforme par défaut ? Toutes vos modifications seront perdues.')) return;
    try {
      const res = await adminApi.resetSAPPageContent();
      setContent(res.data);
      toast.success('Page réinitialisée !');
    } catch (e) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" /></div>;
  if (!content) return <div className="text-center py-12 text-[#5C6A61]">Erreur lors du chargement</div>;

  return (
    <div className="space-y-6 pb-24" data-testid="sap-page-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#2A312C]">Page « Service à la personne / Avance immédiate »</h2>
          <p className="text-sm text-[#5C6A61]">100 % administrable : sections, ordre, visibilité, textes, listes, étapes, FAQ, encadrés, liens, boutons, images, icônes et couleurs.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/sap" target="_blank" rel="noopener noreferrer" className="btn-outline text-sm inline-flex items-center gap-1"><ExternalLink className="w-4 h-4" />Voir la page</a>
          <button onClick={openPreview} className="btn-outline text-sm inline-flex items-center gap-1" data-testid="sap-preview-btn"><Eye className="w-4 h-4" />Aperçu</button>
          <button onClick={handleReset} className="btn-outline text-sm inline-flex items-center gap-1" data-testid="sap-reset-btn"><RotateCcw className="w-4 h-4" />Réinitialiser</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-1" data-testid="sap-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
          </button>
        </div>
      </div>

      {/* Global settings */}
      <div className="card-default space-y-4">
        <h3 className="font-semibold text-[#2A312C]">Paramètres généraux & en-tête</h3>
        <Toggle label="Page activée (visible par le public)" value={content.is_enabled} onChange={(v) => set({ is_enabled: v })} testId="sap-enabled-toggle" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Titre de la page" value={content.page_title} onChange={(v) => set({ page_title: v })} testId="sap-title-input" />
          <Field label="Texte du badge (hero)" value={content.hero_badge_text} onChange={(v) => set({ hero_badge_text: v })} />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2A312C] mb-1 block">Sous-titre</label>
          <textarea value={content.page_subtitle || ''} onChange={(e) => set({ page_subtitle: e.target.value })} className="input-default" rows={2} />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2A312C] mb-1 block">Meta description (SEO)</label>
          <textarea value={content.meta_description || ''} onChange={(e) => set({ meta_description: e.target.value })} className="input-default" rows={2} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Numéro SAP" value={content.sap_number} onChange={(v) => set({ sap_number: v })} />
          <Field label="Texte de déclaration SAP" value={content.sap_declaration_text} onChange={(v) => set({ sap_declaration_text: v })} />
        </div>
        <Toggle label="Afficher le badge du numéro SAP" value={content.show_sap_badge} onChange={(v) => set({ show_sap_badge: v })} />
        <div>
          <label className="text-sm font-medium text-[#2A312C] mb-1 block">Icône du badge (hero) — téléversée</label>
          <div className="max-w-sm"><MediaUpload testIdPrefix="hero-icon" value={content.hero_icon_url} onChange={(url) => set({ hero_icon_url: url })} /></div>
          <div className="mt-2 max-w-xs">
            <label className="text-xs text-[#5C6A61] mb-1 block">…ou icône de la bibliothèque</label>
            <select value={content.hero_icon_name || ''} onChange={(e) => set({ hero_icon_name: e.target.value })} className="input-default">
              <option value="">Aucune</option>
              {SAP_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#2A312C]">Sections de la page ({content.sections?.length || 0})</h3>
        </div>
        {(content.sections || []).map((section, index) => (
          <SectionEditor
            key={section.id || index}
            section={section}
            index={index}
            total={content.sections.length}
            onChange={(updated) => updateSection(index, updated)}
            onMove={moveSection}
            onDelete={deleteSection}
          />
        ))}

        {/* Add section */}
        <div className="card-default flex flex-wrap items-end gap-3">
          <div>
            <label className="text-sm font-medium text-[#2A312C] mb-1 block">Ajouter une section</label>
            <select value={newType} onChange={(e) => setNewType(e.target.value)} className="input-default" data-testid="new-section-type">
              {SECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <button onClick={addSection} className="btn-primary inline-flex items-center gap-1" data-testid="add-section-btn"><Plus className="w-4 h-4" />Ajouter</button>
        </div>
      </div>

      {/* CTA settings */}
      <div className="card-default space-y-4">
        <h3 className="font-semibold text-[#2A312C]">Bloc d'appel à l'action (bas de page)</h3>
        <Toggle label="Afficher le bloc CTA" value={content.show_cta} onChange={(v) => set({ show_cta: v })} testId="sap-cta-toggle" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Titre CTA" value={content.cta_title} onChange={(v) => set({ cta_title: v })} />
          <Field label="Texte du bouton CTA" value={content.cta_button_text} onChange={(v) => set({ cta_button_text: v })} />
          <Field label="Lien du bouton CTA" value={content.cta_button_link} onChange={(v) => set({ cta_button_link: v })} placeholder="/reserver" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2A312C] mb-1 block">Texte CTA</label>
          <textarea value={content.cta_text || ''} onChange={(e) => set({ cta_text: e.target.value })} className="input-default" rows={2} />
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 flex justify-end">
        <button onClick={handleSave} disabled={saving} className="btn-primary shadow-lg inline-flex items-center gap-1">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer les modifications
        </button>
      </div>
    </div>
  );
}
